import { MAX_OPTICAL_JOBS_PER_ORDER, type ApiErrorDetail } from '@optiflow/types';
import type { FieldError } from '@/modules/medical/examinationForm';
import type { BuilderStep } from './draft';

/**
 * What a refusal from the server means for the builder: which pair, which step, which field, in the user's words.
 *
 * The server names every field it refuses by its place in the request — `opticalJobs[1].prescription.left.cylinder`, `lines[4].quantity` — and the builder knows which pair each place is, so a message lands on the field that needs changing. The server's own sentence is never shown. The price check and the checkout name fields the same way, so one reading serves both.
 */

export interface BuilderProblem {
  /** Null for something about the whole order. */
  pairId: string | null;
  /** Where it is fixed. */
  step: BuilderStep;
  /** The screen's field: `rx.right.sphere`, `rx.totalPdMm`, `clinical`, `frame`, `lens.right`, `fitting.left`, `lab`, `order`, `payment`, `credit.installmentCount`. */
  field: string;
  message: FieldError;
}

export interface RefusalLike {
  code: string | null;
  details: ApiErrorDetail[];
}

const EYE_FIELDS = new Set(['sphere', 'cylinder', 'axis', 'add', 'prism', 'prismBase']);
const PRESCRIPTION_FIELDS = new Set([
  'totalPdMm',
  'rightPdMm',
  'leftPdMm',
  'pdType',
  'prescribedOn',
  'expiresOn',
  'externalReference',
  'prescriberName',
]);
const CREDIT_FIELDS = new Set([
  'installmentCount',
  'firstDueDate',
  'interval',
  'intervalDays',
  'overrideReason',
]);
const EYE_OF_ROLE = ['frame', 'right', 'left'] as const;

/** `opticalJobs.0.rightFittingHeightMm must be …` from the request pipe becomes `opticalJobs[0].rightFittingHeightMm`. */
export function fieldOfDetail(detail: ApiErrorDetail): string | null {
  if (detail.field) return detail.field;
  const leading = /^([A-Za-z][\w.]*)\s/.exec(detail.message)?.[1];
  if (!leading || !leading.includes('.')) return leading ?? null;
  return leading.replace(/\.(\d+)(?=\.|$)/g, '[$1]');
}

/**
 * Reads a refusal against the request it answered.
 *
 * `pairIds[i]` is the pair sent as `opticalJobs[i]`; its three lines were sent as `lines[3i]`, `lines[3i + 1]` and `lines[3i + 2]` — frame, right lens, left lens — because the builder always sends them in that order.
 */
export function problemsFromRefusal(
  refusal: RefusalLike,
  pairIds: readonly string[],
): BuilderProblem[] {
  const problems: BuilderProblem[] = [];
  const code = refusal.code ?? '';
  const outOfRange = code === 'PRESCRIPTION_OUT_OF_LENS_RANGE';

  for (const detail of refusal.details) {
    const field = fieldOfDetail(detail);
    if (!field) continue;

    const job = /^opticalJobs\[(\d+)\](?:\.(.+))?$/.exec(field);
    if (job) {
      const pairId = pairIds[Number(job[1])] ?? null;
      const rest = job[2] ?? '';
      problems.push(jobProblem(pairId, rest, outOfRange));
      continue;
    }

    const line = /^lines\[(\d+)\](?:\.(.+))?$/.exec(field);
    if (line) {
      const index = Number(line[1]);
      const pairId = pairIds[Math.floor(index / 3)] ?? null;
      const role = EYE_OF_ROLE[index % 3] ?? 'frame';
      problems.push({
        pairId,
        step: role === 'frame' ? 'frame' : 'lenses',
        field: role === 'frame' ? 'frame' : `lens.${role}`,
        message: {
          key:
            line[2] === 'recommendationToken'
              ? 'opticalOrders.refusals.recommendation'
              : 'opticalOrders.refusals.line',
        },
      });
      continue;
    }

    if (field === 'opticalJobs') {
      problems.push({
        pairId: null,
        step: 'review',
        field: 'order',
        message: {
          key: 'opticalOrders.refusals.tooManyPairs',
          values: { max: String(MAX_OPTICAL_JOBS_PER_ORDER) },
        },
      });
    } else if (field === 'customerId') {
      problems.push({
        pairId: null,
        step: 'review',
        field: 'order',
        message: { key: 'opticalOrders.refusals.customer' },
      });
    } else if (field === 'tenders' || field === 'amountMinor') {
      problems.push({
        pairId: null,
        step: 'payment',
        field: 'payment',
        message: { key: 'opticalOrders.refusals.tenders' },
      });
    } else if (field === 'credit' || CREDIT_FIELDS.has(field.replace(/^credit\./, ''))) {
      const name = field.replace(/^credit\./, '');
      problems.push({
        pairId: null,
        step: 'payment',
        field: CREDIT_FIELDS.has(name) ? `credit.${name}` : 'credit',
        message: { key: creditKey(code, name) },
      });
    }
  }

  return problems;
}

function jobProblem(pairId: string | null, rest: string, outOfRange: boolean): BuilderProblem {
  const eyeField = /^prescription\.(right|left)\.(\w+)$/.exec(rest);
  if (eyeField && EYE_FIELDS.has(eyeField[2] ?? '')) {
    const [, eye, name] = eyeField;
    // The lens cannot be made to this eye: the lens is what changes, not the prescription.
    if (outOfRange) {
      return {
        pairId,
        step: 'lenses',
        field: `lens.${eye}`,
        message: {
          key:
            name === 'add'
              ? 'opticalOrders.refusals.additionForLens'
              : `opticalOrders.refusals.outOfRange.${name === 'cylinder' ? 'cylinder' : 'sphere'}`,
        },
      };
    }
    return {
      pairId,
      step: 'prescription',
      field: `rx.${eye}.${name}`,
      message: { key: 'opticalOrders.refusals.prescriptionField' },
    };
  }

  const prescriptionField = /^prescription\.(\w+)$/.exec(rest)?.[1];
  if (prescriptionField === 'clinicalPrescriptionId') {
    return {
      pairId,
      step: 'prescription',
      field: 'clinical',
      message: { key: 'opticalOrders.clinical.noLongerAvailable' },
    };
  }
  if (prescriptionField && PRESCRIPTION_FIELDS.has(prescriptionField)) {
    return {
      pairId,
      step: 'prescription',
      field: `rx.${prescriptionField}`,
      message: {
        key:
          prescriptionField === 'expiresOn'
            ? 'opticalOrders.refusals.expiresOn'
            : prescriptionField === 'prescribedOn'
              ? 'opticalOrders.refusals.prescribedOn'
              : 'opticalOrders.refusals.prescriptionField',
      },
    };
  }
  if (rest.startsWith('prescription')) {
    return {
      pairId,
      step: 'prescription',
      field: 'prescription',
      message: { key: 'opticalOrders.refusals.prescription' },
    };
  }

  const fitting = /^(right|left)FittingHeightMm$/.exec(rest)?.[1];
  if (fitting) {
    return {
      pairId,
      step: 'lenses',
      field: `fitting.${fitting}`,
      message: { key: 'opticalOrders.refusals.fitting' },
    };
  }
  if (rest === 'labId') {
    return { pairId, step: 'lenses', field: 'lab', message: { key: 'opticalOrders.refusals.lab' } };
  }
  if (rest === 'frameLineKey') {
    return {
      pairId,
      step: 'frame',
      field: 'frame',
      message: { key: 'opticalOrders.refusals.frame' },
    };
  }
  const lensKey = /^(right|left)LensLineKey$/.exec(rest)?.[1];
  if (lensKey) {
    return {
      pairId,
      step: 'lenses',
      field: `lens.${lensKey}`,
      message: { key: 'opticalOrders.refusals.lens' },
    };
  }
  return { pairId, step: 'review', field: 'pair', message: { key: 'opticalOrders.refusals.pair' } };
}

function creditKey(code: string, field: string): string {
  if (code === 'FORBIDDEN') return 'opticalOrders.credit.overrideNotAllowed';
  if (field === 'overrideReason') return 'opticalOrders.credit.overrideReasonRequired';
  if (field === 'installmentCount') return 'opticalOrders.credit.installmentCountRefused';
  if (field === 'firstDueDate') return 'opticalOrders.credit.firstDueDateRefused';
  return 'opticalOrders.credit.refused';
}
